import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useCreateVaultEntry,
  useVaultCategories,
  useVaultSensitivities,
} from "../hooks/useVault";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
};

type EntryType = "credentials" | "external";
type Visibility = "team" | "restricted";

export function NewVaultEntryDrawer({ isOpen, onClose, onCreated }: Props) {
  const create = useCreateVaultEntry();
  const { data: categories = [] } = useVaultCategories();
  const { data: sensitivities = [] } = useVaultSensitivities();
  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [sensitivity, setSensitivity] = useState("medium");
  const [expiryDate, setExpiryDate] = useState("");

  const [entryType, setEntryType] = useState<EntryType>("credentials");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [databaseName, setDatabaseName] = useState("");
  const [notes, setNotes] = useState("");
  const [externalUrl, setExternalUrl] = useState("");

  const [visibility, setVisibility] = useState<Visibility>("team");
  const [restrictedToIds, setRestrictedToIds] = useState<string[]>([]);

  const [companyId, setCompanyId] = useState("");
  const [productId, setProductId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 200,
    companyId: companyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    companyId: companyId || undefined,
    productId: productId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  useEffect(() => {
    setProductId("");
    setProjectId("");
  }, [companyId]);

  useEffect(() => {
    setProjectId("");
  }, [productId]);

  function reset() {
    setTitle("");
    setDescription("");
    setCategory("other");
    setSensitivity("medium");
    setExpiryDate("");
    setEntryType("credentials");
    setUsername("");
    setPassword("");
    setApiKey("");
    setApiSecret("");
    setToken("");
    setHost("");
    setPort("");
    setDatabaseName("");
    setNotes("");
    setExternalUrl("");
    setVisibility("team");
    setRestrictedToIds([]);
    setCompanyId("");
    setProductId("");
    setProjectId("");
    setError(null);
  }

  // Critical: reset every field whenever the drawer (re)opens. Without
  // this, stale state from a previous session — or worse, browser
  // autofill that injected the user's login password into the
  // hidden-but-still-stateful password field — could persist between
  // opens. Mirrors the NewUserDrawer pattern.
  useEffect(() => {
    if (isOpen) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (entryType === "external" && !externalUrl.trim()) {
      setError("External URL is required");
      return;
    }
    setError(null);
    try {
      const isExternal = entryType === "external";
      const res = await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        category,
        sensitivity,
        isExternal,
        externalUrl: isExternal ? externalUrl.trim() : null,
        expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null,
        visibility,
        restrictedToIds: visibility === "restricted" ? restrictedToIds : [],
        username: !isExternal ? username.trim() || null : null,
        password: !isExternal ? password.trim() || null : null,
        apiKey: !isExternal ? apiKey.trim() || null : null,
        apiSecret: !isExternal ? apiSecret.trim() || null : null,
        token: !isExternal ? token.trim() || null : null,
        host: !isExternal ? host.trim() || null : null,
        port: !isExternal ? port.trim() || null : null,
        databaseName: !isExternal ? databaseName.trim() || null : null,
        notes: notes.trim() || null,
        companyId: companyId || null,
        productId: productId || null,
        projectId: projectId || null,
      });
      onCreated?.(res.entry.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create entry");
    }
  }

  function toggleRestrictedUser(userId: string) {
    setRestrictedToIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            New Vault Entry
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
          autoComplete="off"
        >
          {/* Honeypot fields — Chrome ignores autoComplete="off" on
              password inputs paired with username-ish fields, but
              reliably autofills the FIRST username/password pair it
              sees. These hidden, focusless dummies absorb the autofill
              so the real credential inputs below stay empty. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: "absolute",
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: "none",
            }}
          />

          <div className="flex-1 space-y-6 p-6">
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Entry Details
              </h3>
              <div className="space-y-2">
                <Label htmlFor="vlt-title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="vlt-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Production DB"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="vlt-cat">Category</Label>
                  <Select
                    id="vlt-cat"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categories.length === 0 ? (
                      <option value="other">Other</option>
                    ) : (
                      categories.map((c) => (
                        <option key={c.id} value={c.value}>
                          {c.label}
                        </option>
                      ))
                    )}
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vlt-sens">Sensitivity</Label>
                  <Select
                    id="vlt-sens"
                    value={sensitivity}
                    onChange={(e) => setSensitivity(e.target.value)}
                  >
                    {sensitivities.length === 0 ? (
                      <option value="medium">Medium</option>
                    ) : (
                      sensitivities.map((s) => (
                        <option key={s.id} value={s.value}>
                          {s.label}
                        </option>
                      ))
                    )}
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vlt-desc">Description</Label>
                <textarea
                  id="vlt-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  style={{ borderColor: "hsl(210,30%,85%)" }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vlt-expiry">Expiry Date</Label>
                <Input
                  id="vlt-expiry"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>
            </section>

            <section className="space-y-3">
              <div
                className="inline-flex rounded-md border bg-muted p-0.5"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                <button
                  type="button"
                  onClick={() => setEntryType("credentials")}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${
                    entryType === "credentials"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  Store Credentials
                </button>
                <button
                  type="button"
                  onClick={() => setEntryType("external")}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${
                    entryType === "external"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  External Link
                </button>
              </div>

              {entryType === "credentials" ? (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="vlt-username">Username</Label>
                    <Input
                      id="vlt-username"
                      name="vlt-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                    />
                  </div>
                  <PasswordField
                    id="vlt-password"
                    label="Password"
                    value={password}
                    onChange={setPassword}
                    show={showPassword}
                    onToggleShow={() => setShowPassword((v) => !v)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="vlt-apikey">API Key</Label>
                    <Input
                      id="vlt-apikey"
                      name="vlt-apikey"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      data-1p-ignore="true"
                    />
                  </div>
                  <PasswordField
                    id="vlt-apisecret"
                    label="API Secret"
                    value={apiSecret}
                    onChange={setApiSecret}
                    show={showApiSecret}
                    onToggleShow={() => setShowApiSecret((v) => !v)}
                  />
                  <PasswordField
                    id="vlt-token"
                    label="Token"
                    value={token}
                    onChange={setToken}
                    show={showToken}
                    onToggleShow={() => setShowToken((v) => !v)}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="vlt-host">Host / URL</Label>
                    <Input
                      id="vlt-host"
                      value={host}
                      onChange={(e) => setHost(e.target.value)}
                      placeholder="db.example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="vlt-port">Port</Label>
                      <Input
                        id="vlt-port"
                        type="number"
                        value={port}
                        onChange={(e) => setPort(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vlt-db">Database Name</Label>
                      <Input
                        id="vlt-db"
                        value={databaseName}
                        onChange={(e) => setDatabaseName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vlt-notes">Notes (encrypted)</Label>
                    <textarea
                      id="vlt-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      style={{ borderColor: "hsl(210,30%,85%)" }}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="vlt-ext-url">
                      External URL <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="vlt-ext-url"
                      type="url"
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vlt-ext-notes">
                      Notes (where to find it, what it accesses)
                    </Label>
                    <textarea
                      id="vlt-ext-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      style={{ borderColor: "hsl(210,30%,85%)" }}
                    />
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Visibility
              </h3>
              <div
                className="inline-flex rounded-md border bg-muted p-0.5"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                <button
                  type="button"
                  onClick={() => setVisibility("team")}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${
                    visibility === "team"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  Team
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility("restricted")}
                  className={`px-3 py-1.5 text-xs font-medium rounded ${
                    visibility === "restricted"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  Restricted
                </button>
              </div>
              {visibility === "restricted" && (
                <div className="space-y-2">
                  <Label>Select users who can access this entry</Label>
                  <div
                    className="max-h-[160px] overflow-y-auto rounded-md border p-2"
                    style={{ borderColor: "hsl(210,30%,85%)" }}
                  >
                    {allUsers.length === 0 ? (
                      <div className="text-xs text-muted-foreground p-2">
                        No users available
                      </div>
                    ) : (
                      allUsers.map((u) => (
                        <label
                          key={u.id}
                          className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-muted/40 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={restrictedToIds.includes(u.id)}
                            onChange={() => toggleRestrictedUser(u.id)}
                            className="h-3.5 w-3.5"
                          />
                          <span>{u.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">
                Association
              </h3>
              <div className="space-y-2">
                <Label htmlFor="vlt-company">Company</Label>
                <Select
                  id="vlt-company"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vlt-product">Product</Label>
                <Select
                  id="vlt-product"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  disabled={!companyId}
                >
                  <option value="">— None —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vlt-project">Project</Label>
                <Select
                  id="vlt-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
            </section>

            {error && (
              <div className="rounded-md border bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}
          </div>

          <div
            className="border-t bg-white p-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <div
              className="mb-3 flex items-start gap-2 text-[11px] text-muted-foreground"
              style={{ lineHeight: 1.4 }}
            >
              <Lock className="h-3 w-3 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>
                All credentials are encrypted with AES-256 before storage.
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create Entry"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // autoComplete="new-password" — counter-intuitively the most
          // reliable way to stop Chrome from filling the user's saved
          // login password. Combined with the honeypot fields above and
          // the lp/1p/form-type opt-outs, this keeps the field empty on
          // open and on edit.
          autoComplete="new-password"
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore="true"
          className="pr-9"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
          aria-label={show ? "Hide" : "Show"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
